import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Send, Loader2, CheckCircle2 } from "lucide-react";

const services = [
  "Life Coaching",
  "Personal Training",
  "Menopause Coaching",
  "Temple Spa Skincare",
  "Online Group Fitness",
  "The MNE Formula",
  "Not sure yet",
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  useEffect(() => {
    const handler = (e) => {
      if (e.detail) setForm((f) => ({ ...f, service: e.detail }));
    };
    window.addEventListener("preselect-service", handler);
    return () => window.removeEventListener("preselect-service", handler);
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      await res.json();
      setStatus("success");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      setStatus("error");
      setError("Something went wrong. Please email wellness@jennie-marie.com directly.");
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-cream/25 py-3 text-cream placeholder:text-cream/40 focus:outline-none focus:border-mne-pink transition-colors duration-300";

  if (status === "success") {
    return (
      <motion.div
        data-testid="contact-form-success"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col items-start gap-4 rounded-[8px] border border-cream/20 bg-cream/5 p-10"
      >
        <CheckCircle2 size={40} className="text-mne-pink" strokeWidth={1.5} />
        <h3 className="font-serif text-3xl text-cream">Thank you, lovely.</h3>
        <p className="text-cream/70 max-w-sm">
          Your message is on its way to Jennie-Marie. She'll be in touch within 1–2 working days to
          arrange your free discovery call.
        </p>
        <button
          data-testid="contact-form-reset"
          onClick={() => setStatus("idle")}
          className="mt-2 text-[12px] uppercase tracking-[0.18em] text-mne-pink border-b border-mne-pink/40 pb-1 hover:border-mne-pink transition-colors"
        >
          Send another message
        </button>
      </motion.div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={submit} className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] text-cream/60">Name</label>
          <input data-testid="contact-input-name" required value={form.name} onChange={update("name")} placeholder="Your name" className={inputClass} />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] text-cream/60">Email</label>
          <input data-testid="contact-input-email" required type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" className={inputClass} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] text-cream/60">Phone (optional)</label>
          <input data-testid="contact-input-phone" value={form.phone} onChange={update("phone")} placeholder="07…" className={inputClass} />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] text-cream/60">Interested in</label>
          <select data-testid="contact-input-service" value={form.service} onChange={update("service")} className={`${inputClass} [&>option]:text-plum`}>
            <option value="">Select a service…</option>
            {services.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="text-[11px] uppercase tracking-[0.18em] text-cream/60">Message</label>
        <textarea data-testid="contact-input-message" required rows={3} value={form.message} onChange={update("message")} placeholder="Tell me a little about your goals…" className={`${inputClass} resize-none`} />
      </div>

      {status === "error" && (
        <p data-testid="contact-form-error" className="text-sm text-mne-pink">{error}</p>
      )}

      <button
        data-testid="contact-form-submit"
        type="submit"
        disabled={status === "loading"}
        className="group inline-flex items-center gap-3 rounded-full bg-cream px-8 py-4 text-[12px] uppercase tracking-[0.18em] text-plum transition-colors duration-300 hover:bg-mne-pink disabled:opacity-60"
      >
        {status === "loading" ? (
          <>Sending<Loader2 size={16} className="animate-spin" /></>
        ) : (
          <>Send my enquiry<Send size={15} className="transition-transform duration-300 group-hover:translate-x-1" /></>
        )}
      </button>
    </form>
  );
}
